import { useEffect, useState } from "react";
import { Row, Card, CardBody, Table } from "reactstrap";
import { motion } from "framer-motion";
import ReactApexChart from "react-apexcharts";

import { getMcapContentSummary, getMcapStateTrends, getMcapUsageTrends, getMcapActivityRanking } from "api/api.service";
import { getCached, setCached, mcapCacheKey } from "utils/mcapCache";
import KpiTile from "../components/KpiTile";
import EmptyState from "../components/EmptyState";
import typography from "../typography";

const panelVariants = { hidden: { opacity: 0, scale: 0.98 }, visible: { opacity: 1, scale: 1, transition: { duration: 0.3, ease: [0.23, 1, 0.32, 1] } } };

// Fixed axis order — schema order for state, a "story of the school day" order
// for context — rather than whatever order the API happens to return groups
// in, so the grid reads the same shape on every load.
const STATE_KEYS = ["calm", "focus", "clarity", "energy", "comfort", "UNSPECIFIED"];
const CONTEXTS = ["MORNING_MEETING", "TRANSITION", "BEFORE_TEST", "AFTER_LUNCH", "RETURN_FROM_RECESS", "UNSPECIFIED"];
const STATE_LABELS = { calm: "Calm", focus: "Focus", clarity: "Clarity", energy: "Energy", comfort: "Comfort", UNSPECIFIED: "Unspecified" };
const CONTEXT_LABELS = {
  MORNING_MEETING: "Morning meeting",
  TRANSITION: "Transition",
  BEFORE_TEST: "Before test",
  AFTER_LUNCH: "After lunch",
  RETURN_FROM_RECESS: "Return from recess",
  UNSPECIFIED: "Not specified",
};

// A13b suppression sentinel and the true-zero sentinel are both negative /
// distinct from any real count (counts are always >= 1 once a cell is
// suppressed, since a suppressed cell is 1-4, and >=5 once shown) — kept far
// apart from real data so the heatmap's color ranges below can never mistake
// one for the other.
const NO_DATA = 0;
const INSUFFICIENT_DATA = -1;

// Distinct, deliberately un-gradient-like colors for the two "not a real
// count" states, so neither can be mistaken for the low end of the real
// gradient — the exact confusion the brief's "never zero-fill" rule exists
// to prevent. Real counts get the same green ramp used elsewhere in MCAP.
const HEATMAP_COLOR_RANGES = [
  { from: INSUFFICIENT_DATA, to: INSUFFICIENT_DATA, name: typography.STATE_TRENDS_INSUFFICIENT, color: "#c9c9c9" },
  { from: NO_DATA, to: NO_DATA, name: typography.STATE_TRENDS_NO_DATA, color: "#f3f3f3" },
  { from: 1, to: 5, name: "1-5", color: "#e3f3de" },
  { from: 6, to: 15, name: "6-15", color: "#a9d8a3" },
  { from: 16, to: 30, name: "16-30", color: "#58af54" },
  { from: 31, to: 100, name: "31+", color: "#06402b" },
];

// cells: [{ stateKey, context|hour, count, suppressed }]; colKey/colLabels
// describe the x-axis (context enum values, or 0-23 for hour). Every
// (state, col) combination the API didn't return at all becomes NO_DATA, not
// a guess — the API only omits a cell when zero resets landed there.
function buildHeatmapSeries(cells, colKeys, colLabels, colField) {
  const byState = new Map(STATE_KEYS.map((s) => [s, new Map()]));
  for (const cell of cells || []) {
    const stateMap = byState.get(cell.stateKey);
    if (!stateMap) continue; // an enum value neither list above knows about — skip rather than guess a label
    stateMap.set(cell[colField], cell.suppressed ? INSUFFICIENT_DATA : cell.count);
  }
  return STATE_KEYS.map((stateKey) => ({
    name: STATE_LABELS[stateKey],
    data: colKeys.map((col) => ({ x: colLabels[col], y: byState.get(stateKey).has(col) ? byState.get(stateKey).get(col) : NO_DATA })),
  }));
}

function heatmapOptions() {
  return {
    chart: { toolbar: { show: false } },
    dataLabels: { enabled: false },
    plotOptions: { heatmap: { colorScale: { ranges: HEATMAP_COLOR_RANGES } } },
    xaxis: { labels: { style: { fontSize: "11px", fontFamily: "Poppins" } } },
    yaxis: { labels: { style: { fontSize: "12px", fontFamily: "Poppins" } } },
    tooltip: {
      y: {
        formatter: (val) => (val === INSUFFICIENT_DATA ? typography.STATE_TRENDS_INSUFFICIENT : val === NO_DATA ? typography.STATE_TRENDS_NO_DATA : `${val} resets`),
      },
    },
    legend: { show: false },
  };
}

export default function ResetsAndContent({ filters, filterBar }) {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [stateTrends, setStateTrends] = useState(null);
  const [stateTrendsLoading, setStateTrendsLoading] = useState(true);
  const [usageTrends, setUsageTrends] = useState(null);
  const [usageTrendsLoading, setUsageTrendsLoading] = useState(true);
  const [ranking, setRanking] = useState(null);
  const [rankingLoading, setRankingLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const cacheKey = mcapCacheKey("content", filters);
    const cached = getCached(cacheKey);
    if (cached) {
      setData(cached);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    getMcapContentSummary(filters)
      .then((res) => {
        if (cancelled) return;
        setData(res || {});
        setCached(cacheKey, res);
      })
      .catch(() => {
        if (!cancelled) setData({});
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters)]);

  useEffect(() => {
    let cancelled = false;
    const cacheKey = mcapCacheKey("state-trends", filters);
    const cached = getCached(cacheKey);
    if (cached) {
      setStateTrends(cached);
      setStateTrendsLoading(false);
      return undefined;
    }
    setStateTrendsLoading(true);
    getMcapStateTrends(filters)
      .then((res) => {
        if (cancelled) return;
        setStateTrends(res || null);
        setCached(cacheKey, res);
      })
      .catch(() => {
        if (!cancelled) setStateTrends(null);
      })
      .finally(() => {
        if (!cancelled) setStateTrendsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters)]);

  useEffect(() => {
    let cancelled = false;
    const cacheKey = mcapCacheKey("usage-trends", filters);
    const cached = getCached(cacheKey);
    if (cached) {
      setUsageTrends(cached);
      setUsageTrendsLoading(false);
      return undefined;
    }
    setUsageTrendsLoading(true);
    getMcapUsageTrends(filters)
      .then((res) => {
        if (cancelled) return;
        setUsageTrends(res || null);
        setCached(cacheKey, res);
      })
      .catch(() => {
        if (!cancelled) setUsageTrends(null);
      })
      .finally(() => {
        if (!cancelled) setUsageTrendsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters)]);

  useEffect(() => {
    let cancelled = false;
    const cacheKey = mcapCacheKey("activity-ranking", filters);
    const cached = getCached(cacheKey);
    if (cached) {
      setRanking(cached);
      setRankingLoading(false);
      return undefined;
    }
    setRankingLoading(true);
    getMcapActivityRanking(filters)
      .then((res) => {
        if (cancelled) return;
        setRanking(res || null);
        setCached(cacheKey, res);
      })
      .catch(() => {
        if (!cancelled) setRanking(null);
      })
      .finally(() => {
        if (!cancelled) setRankingLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters)]);

  const hasRanking = !!ranking && ranking.mostUsed.length > 0;

  const hasUsageTrends = !!usageTrends && usageTrends.totalResets > 0;
  const usageByHourVisible = (usageTrends?.byHour || []).filter((c) => !c.suppressed);
  const usageByHourSuppressedCount = (usageTrends?.byHour || []).filter((c) => c.suppressed).length;
  const usageByContextVisible = (usageTrends?.byContext || []).filter((c) => !c.suppressed);
  const usageByContextSuppressedCount = (usageTrends?.byContext || []).filter((c) => c.suppressed).length;

  const hasStateTrends = !!stateTrends && stateTrends.totalRecords > 0;
  const hourLabels = Object.fromEntries(Array.from({ length: 24 }, (_, h) => [h, `${h}:00`]));
  const hourKeys = Array.from({ length: 24 }, (_, h) => h);

  return (
    <div className="p-4 mt-3">
      {filterBar}
      <Row>
        <KpiTile index={0} label={typography.BLS_COMPLETED} value={data.blsCompletedCount} loading={loading} colorClass="primary-gradient-bg" />
        <KpiTile
          index={1}
          label={typography.BLS_AVG_DURATION}
          value={data.blsAvgDurationSeconds ? Math.round(data.blsAvgDurationSeconds / 60) : 0}
          suffix=" min"
          loading={loading}
          colorClass="bg-orange"
        />
        <KpiTile index={2} label={typography.BLS_AVG_RATING} value={data.blsAvgRating ?? 0} suffix="/5" loading={loading} colorClass="bg-blue" />
        <KpiTile index={3} label={typography.CLASS_INITIATED} value={data.classInitiatedActivityCount} loading={loading} colorClass="bg-purple" />
      </Row>

      <motion.div initial="hidden" animate="visible" variants={panelVariants}>
        <EmptyState title={typography.PER_ACTIVITY_EMPTY} detail={typography.PER_ACTIVITY_EMPTY_DETAIL} />
      </motion.div>

      {hasRanking && (
        <Row>
          <div className="col-xl-6 col-12">
            <motion.div initial="hidden" animate="visible" variants={panelVariants}>
              <Card className="mt-3">
                <CardBody>
                  <h5 className="mb-3 font-family-lexend standard-typo-14-500-ld">{typography.MOST_USED_TITLE}</h5>
                  <div className="table-responsive">
                    <Table borderless className="mb-0">
                      <thead>
                        <tr>
                          <th>{typography.RANKING_COL_ACTIVITY}</th>
                          <th className="text-end">{typography.RANKING_COL_USES}</th>
                          <th className="text-end">{typography.RANKING_COL_RATING}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ranking.mostUsed.map((item) => (
                          <tr key={item.key}>
                            <td>{item.title}</td>
                            <td className="text-end">{item.uses}</td>
                            <td className="text-end text-muted">
                              {item.suppressed ? "—" : `${item.avgUsefulness}/10 (n=${item.ratedCount})`}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </motion.div>
          </div>
          <div className="col-xl-6 col-12">
            <motion.div initial="hidden" animate="visible" variants={panelVariants}>
              <Card className="mt-3">
                <CardBody>
                  <h5 className="mb-1 font-family-lexend standard-typo-14-500-ld">{typography.MOST_HELPFUL_TITLE}</h5>
                  <p className="standard-typo-12-400-ld mcap-chart-subtitle mb-3">{typography.MOST_HELPFUL_SUBTITLE}</p>
                  {ranking.mostHelpful.length === 0 ? (
                    <p className="standard-typo-12-400-ld mcap-chart-subtitle mb-0">{typography.RANKING_EMPTY}</p>
                  ) : (
                    <div className="table-responsive">
                      <Table borderless className="mb-0">
                        <thead>
                          <tr>
                            <th>{typography.RANKING_COL_ACTIVITY}</th>
                            <th className="text-end">{typography.RANKING_COL_RATING}</th>
                            <th className="text-end">{typography.RANKING_COL_N}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ranking.mostHelpful.map((item) => (
                            <tr key={item.key}>
                              <td>{item.title}</td>
                              <td className="text-end">{item.avgUsefulness}/10</td>
                              <td className="text-end text-muted">{item.ratedCount}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </CardBody>
              </Card>
            </motion.div>
          </div>
        </Row>
      )}

      {!usageTrendsLoading && !hasUsageTrends && (
        <motion.div initial="hidden" animate="visible" variants={panelVariants}>
          <EmptyState title={typography.USAGE_TRENDS_EMPTY} detail={typography.USAGE_TRENDS_EMPTY_DETAIL} />
        </motion.div>
      )}

      {(usageTrendsLoading || hasUsageTrends) && (
        <motion.div initial="hidden" animate="visible" variants={panelVariants}>
          <Card className="mt-3">
            <CardBody>
              <h5 className="mb-3 font-family-lexend standard-typo-14-500-ld">{typography.USAGE_TRENDS_DAILY_TITLE}</h5>
              <ReactApexChart
                type="line"
                height={260}
                series={[{ name: "Resets", data: (usageTrends?.byDay || []).map((d) => d.count) }]}
                options={{
                  chart: { toolbar: { show: false } },
                  colors: ["#06402b"],
                  dataLabels: { enabled: false },
                  stroke: { curve: "smooth", width: 2 },
                  connectNulls: false,
                  xaxis: {
                    categories: (usageTrends?.byDay || []).map((d) => d.date),
                    labels: { show: false },
                    axisBorder: { show: false },
                    axisTicks: { show: false },
                  },
                  yaxis: { min: 0 },
                  grid: { borderColor: "#f6f6f6" },
                }}
              />
            </CardBody>
          </Card>
        </motion.div>
      )}

      {hasUsageTrends && (
        <Row>
          <div className="col-xl-6 col-12">
            <motion.div initial="hidden" animate="visible" variants={panelVariants}>
              <Card className="mt-3">
                <CardBody>
                  <h5 className="mb-1 font-family-lexend standard-typo-14-500-ld">{typography.USAGE_TRENDS_BY_HOUR_TITLE}</h5>
                  {usageByHourSuppressedCount > 0 && (
                    <p className="standard-typo-12-400-ld mcap-chart-subtitle mb-2">
                      {usageByHourSuppressedCount} hour(s) hidden — {typography.STATE_TRENDS_INSUFFICIENT.toLowerCase()}.
                    </p>
                  )}
                  <ReactApexChart
                    type="bar"
                    height={240}
                    series={[{ name: "Resets", data: usageByHourVisible.map((c) => c.count) }]}
                    options={{
                      chart: { toolbar: { show: false } },
                      plotOptions: { bar: { borderRadius: 3, columnWidth: "60%" } },
                      colors: ["#06402b"],
                      dataLabels: { enabled: false },
                      xaxis: {
                        categories: usageByHourVisible.map((c) => `${c.hour}:00`),
                        labels: { style: { fontSize: "10px", fontFamily: "Poppins" }, rotate: -45 },
                        axisBorder: { show: false },
                        axisTicks: { show: false },
                      },
                      grid: { borderColor: "#f6f6f6" },
                    }}
                  />
                </CardBody>
              </Card>
            </motion.div>
          </div>
          <div className="col-xl-6 col-12">
            <motion.div initial="hidden" animate="visible" variants={panelVariants}>
              <Card className="mt-3">
                <CardBody>
                  <h5 className="mb-1 font-family-lexend standard-typo-14-500-ld">{typography.USAGE_TRENDS_BY_CONTEXT_TITLE}</h5>
                  {usageByContextSuppressedCount > 0 && (
                    <p className="standard-typo-12-400-ld mcap-chart-subtitle mb-2">
                      {usageByContextSuppressedCount} context(s) hidden — {typography.STATE_TRENDS_INSUFFICIENT.toLowerCase()}.
                    </p>
                  )}
                  <ReactApexChart
                    type="bar"
                    height={240}
                    series={[{ name: "Resets", data: usageByContextVisible.map((c) => c.count) }]}
                    options={{
                      chart: { toolbar: { show: false } },
                      plotOptions: { bar: { borderRadius: 3, columnWidth: "50%" } },
                      colors: ["#2b8a6b"],
                      dataLabels: { enabled: false },
                      xaxis: {
                        categories: usageByContextVisible.map((c) => CONTEXT_LABELS[c.context] || c.context),
                        labels: { style: { fontSize: "10px", fontFamily: "Poppins" }, rotate: -45 },
                        axisBorder: { show: false },
                        axisTicks: { show: false },
                      },
                      grid: { borderColor: "#f6f6f6" },
                    }}
                  />
                </CardBody>
              </Card>
            </motion.div>
          </div>
        </Row>
      )}

      {!stateTrendsLoading && !hasStateTrends && (
        <motion.div initial="hidden" animate="visible" variants={panelVariants}>
          <EmptyState title={typography.STATE_TRENDS_EMPTY} detail={typography.STATE_TRENDS_EMPTY_DETAIL} />
        </motion.div>
      )}

      {(stateTrendsLoading || hasStateTrends) && (
        <motion.div initial="hidden" animate="visible" variants={panelVariants}>
          <Card className="mt-3">
            <CardBody>
              <h5 className="mb-1 font-family-lexend standard-typo-14-500-ld">{typography.STATE_TRENDS_TITLE}</h5>
      
              {!stateTrendsLoading && (
                <p className="standard-typo-12-400-ld mcap-chart-subtitle mb-3">
                  <span className="mcap-legend-swatch" style={{ background: "#c9c9c9" }} /> {typography.STATE_TRENDS_INSUFFICIENT}
                  <span className="mcap-legend-swatch ms-3" style={{ background: "#f3f3f3" }} /> {typography.STATE_TRENDS_NO_DATA}
                </p>
              )}
              <ReactApexChart
                type="heatmap"
                height={320}
                series={hasStateTrends ? buildHeatmapSeries(stateTrends.byContext, CONTEXTS, CONTEXT_LABELS, "context") : []}
                options={heatmapOptions()}
              />
            </CardBody>
          </Card>
        </motion.div>
      )}

      {(stateTrendsLoading || hasStateTrends) && (
        <motion.div initial="hidden" animate="visible" variants={panelVariants}>
          <Card className="mt-3">
            <CardBody>
              <h5 className="mb-3 font-family-lexend standard-typo-14-500-ld">{typography.STATE_TRENDS_BY_HOUR_TITLE}</h5>
              <ReactApexChart
                type="heatmap"
                height={320}
                series={hasStateTrends ? buildHeatmapSeries(stateTrends.byHour, hourKeys, hourLabels, "hour") : []}
                options={heatmapOptions()}
              />
            </CardBody>
          </Card>
        </motion.div>
      )}
    </div>
  );
}
